import { endOfDay, startOfDay } from 'date-fns';
import React, { useState, useEffect, useCallback, CSSProperties } from 'react';
import { useOpen } from '@src/hooks/useOpen';
import { classnames } from '@src/utils/classnames';
import { useDebounce } from '@src/hooks/useDebounce';
import { DatePicker } from '@src/atoms/DatePicker';

import type { ReactDatePickerProps } from 'react-datepicker';
import { getDateFiltersUpdate, updateDateRangeFilterValue } from './utils';
import { DEFAULT_DATEPICKER_DEBOUNCE_TIME } from './consts';

import styles from './FiltersDateRange.scss';
import { useSyncFilterValue } from '../useSyncValue';
import { FiltersController } from '../FiltersController';
import { TSize } from '@src/models/common';

interface IProps {
  controller: FiltersController;
  filterName?: string;
  className?: string;
  style?: CSSProperties;
  popperPlacement?: ReactDatePickerProps['popperPlacement'];
  helperDropdownText?: string;
  isInModal?: boolean;
  disabled?: boolean;
  staticLabel?: string;
  modalSize?: TSize;
}

export const FiltersDateRange: React.FC<IProps> = ({
  controller,
  className,
  style,
  isInModal = false,
  disabled = false,
  popperPlacement = 'bottom-start',
  helperDropdownText,
  filterName = 'startDate',
  staticLabel = 'Период',
  modalSize = 'lg',
}) => {
  const [dateFrom, setDateFrom] = useState<string | null>('');
  const [dateTo, setDateTo] = useState<string | null>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const predicate = useSyncFilterValue({ controller });
  const { open, onOpen, onClose } = useOpen();

  const rootClass = classnames(styles.root, open && styles.open, isInModal && styles.inModal, className);

  const handleChangeDate = useDebounce((dates: [string, string]) => {
    let [start, end] = dates;

    const dateStart = new Date(start);
    const dateEnd = new Date(end);

    start = startOfDay(dateStart).toISOString();
    end = endOfDay(dateEnd).toISOString();

    const updatedFilters = getDateFiltersUpdate({
      predicate,
      dateFrom: start,
      dateTo: end,
      filterName,
    });

    controller.updateDefaultValues(updatedFilters);

    setDateFrom(start);
    setDateTo(end);
  }, DEFAULT_DATEPICKER_DEBOUNCE_TIME);

  const onChange = useCallback(
    (dates: [string | null, string | null]) => {
      const [start, end] = dates;

      setStartDate(start ? new Date(start) : null);
      setEndDate(end ? new Date(end) : null);

      if (start && end) {
        handleChangeDate([start, end]);
      }
    },
    [handleChangeDate],
  );

  useEffect(() => {
    if (!predicate.length) {
      setDateFrom('');
      setDateTo('');
    }

    updateDateRangeFilterValue({ predicate, setDateFrom, setDateTo });
  }, [predicate]);

  useEffect(() => {
    if (dateFrom && dateTo) {
      setStartDate(new Date(dateFrom));
      setEndDate(new Date(dateTo));
    }

    if (!dateFrom && !dateTo) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [dateFrom, dateTo]);

  /** Делаем пробелом для модалки, чтобы скрыть лейбл, который проставляется в закрытом состоянии */
  const rangeStaticLabel = isInModal ? ' ' : staticLabel;

  return (
    <DatePicker
      id="datePicker"
      staticLabel={rangeStaticLabel}
      selectsRange
      startDate={startDate}
      endDate={endDate}
      onChange={onChange}
      className={rootClass}
      onCalendarClose={onClose}
      onCalendarOpen={onOpen}
      popperPlacement={popperPlacement}
      postIcon="triangle"
      helperDropdownText={helperDropdownText}
      style={style}
      showRangeLabel={isInModal}
      size={isInModal ? modalSize : 'sm'}
      disabled={disabled}
    />
  );
};
